import { Directive, inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

/**
 * This directive enables composite form submission by ensuring that when the parent form is submitted,
 * all child forms (managed via `CompositeFormDirective`) also trigger their submission.
 *
 * It listens for the `ngSubmit` event of the parent `FormGroupDirective` and propagates it
 * to the child form directive, ensuring validation and submission logic are executed uniformly.
 *
 * This is particularly useful for handling complex, nested forms where multiple form groups
 * need to behave as a single cohesive unit during submission.
 */
@Directive({
  selector: '[rmsCompositeForm]',
})
export class RmsCompositeFormSubmissionDirective implements OnInit, OnDestroy {
  protected readonly parentFormGroupDirective = inject(FormGroupDirective, { skipSelf: true, optional: true });
  protected readonly formGroupDirective = inject(FormGroupDirective, { self: true });

  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    if (this.parentFormGroupDirective) {
      this.parentFormGroupDirective.ngSubmit
        .pipe(
          tap((event: Event) => {
            this.formGroupDirective.ngSubmit.emit();
            this.formGroupDirective.onSubmit(event);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe();
    }
  }
}
